import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { map } from 'rxjs/operators';
import { PaymentMethod, PaymentData } from '@models/payment-method';
import { AutoTopUp } from '@models/auto-topup';
import { StripeSubscription, StripeSubscriptions } from '@models/stripe-subscription';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly baseUrl;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getPaymentMethod(): Observable<PaymentMethod> {
    return this.http.get<Response>(this.baseUrl + 'payments/method').pipe(map(data => PaymentMethod.deserialize(data['payment-method'])));
  }

  getPaymentMethodOnCart(): Observable<PaymentData> {
    return this.http.get<Response>(this.baseUrl + 'shop/cart/paymentData').pipe(map(data => PaymentData.deserialize(data)));
  }

  getPaymentMethodsGateway(): Observable<PaymentMethod> {
    return this.http
      .get<Response>(this.baseUrl + 'payments/method/gateways')
      .pipe(map(data => PaymentMethod.deserialize(data['payment-method'])));
  }

  addPaymentMethod(stripePaymentMethod): Observable<PaymentMethod> {
    return this.http
      .post(this.baseUrl + 'payments/method', {
        'stripe-payment-method': stripePaymentMethod
      })
      .pipe(map(data => PaymentMethod.deserialize(data)));
  }

  updatePaymentMethod(stripePaymentMethod): Observable<PaymentMethod> {
    return this.http
      .put(this.baseUrl + 'payments/method', {
        'stripe-payment-method': stripePaymentMethod
      })
      .pipe(map(data => PaymentMethod.deserialize(data)));
  }

  deletePaymentMethod(): Observable<void> {
    return this.http.delete<void>(this.baseUrl + `payments/method`);
  }

  getStripeKey(): Observable<string> {
    return this.http.get<Response>(this.baseUrl + 'payments/stripe/public-key').pipe(map(data => data['stripe-public-key']));
  }

  paypalCreatePayment(params, idShoppingCart): Promise<any> {
    const data = {
      idShopCart: idShoppingCart,
      userNotes: '',
      expressCheckoutReturnUrl: params['payment-gateway-params'].return,
      expressCheckoutErrorUrl: params['payment-gateway-params'].cancel_return
    };

    return this.http.post(this.baseUrl + 'shop/paypal/payment/create', data).toPromise();
  }

  paypalExecutePayment(data): Promise<any> {
    return this.http.post(this.baseUrl + 'shop/paypal/payment/execute', data).toPromise();
  }

  createSetupIntent() {
    return this.http.post(this.baseUrl + 'payments/intent', {});
  }

  getStripeSubscriptions(pageNum?, pageSize?): Observable<StripeSubscriptions> {
    let params = new HttpParams();
    if (pageNum) {
      params = params.append('page', pageNum);
    }
    if (pageSize) {
      params = params.append('size', pageSize);
    }
    return this.http
      .get(this.baseUrl + 'subscription', {
        params
      })
      .pipe(
        map(data => {
          return StripeSubscriptions.deserialize({
            data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 =>
              StripeSubscription.deserialize(data2)
            ),
            total: (data as any).total
          });
        })
      );
  }

  deleteStripeSubscription(subscription: StripeSubscription): Observable<void> {
    return this.http.delete<void>(this.baseUrl + `subscription/` + subscription.idSubscription);
  }

  createUserPayment(stripePaymentMethodId, idShopCart): Observable<PaymentMethod> {
    return this.http
      .post(this.baseUrl + 'payments/payment', {
        'payment-method-id': stripePaymentMethodId,
        'id-shop-cart': idShopCart
      })
      .pipe(map(data => PaymentMethod.deserialize(data)));
  }

  getAutoTopUpConfig(): Observable<AutoTopUp> {
    return this.http.get<Response>(this.baseUrl + 'alerts/auto-topup').pipe(map(data => AutoTopUp.deserialize(data)));
  }

  editAutoTopUpConfig(data: AutoTopUp) {
    return this.http.put(this.baseUrl + 'alerts/auto-topup', data, {
      responseType: 'text' as 'json'
    });
  }

  getPaypalParams(): Observable<AutoTopUp> {
    return this.http
      .get<Response>(this.baseUrl + 'shop/paypal/params?paymentGateway=PAYPAL')
      .pipe(map(data => AutoTopUp.deserialize(data)));
  }
}
